import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, resolveApiUrl } from "@/lib/api";
import { LivePlayer } from "@/components/LivePlayer";
import { BuildTimer } from "@/components/BuildTimer";
import { ChatBlockCustomer } from "@/components/ChatBlockCustomer";
import { RatingSection } from "@/components/RatingSection";
import { CustomerTestingPanel } from "@/components/CustomerTestingPanel";
import { TestingVideoOverlay } from "@/components/TestingVideoOverlay";
import { LangProvider, useLang, readStoredLang, writeStoredLang } from "@/lib/i18n";
import {
  CheckCircle2,
  ShieldCheck,
  ClipboardList,
  MonitorSmartphone,
  User,
  MessageSquare,
  Send,
  Paperclip,
  Clock,
  Cpu,
  Zap,
  HardDrive,
  MemoryStick,
  CircuitBoard,
  Package,
  Fan,
  ShieldAlert,
  Lock,
  HelpCircle,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

// ---------- Header pieces ----------
function BrandRow() {
  return (
    <div className="flex items-center gap-4 md:gap-5" data-testid="brand-row">
      <img
        src="/idea-logo.png"
        alt="IDEA"
        className="w-[120px] md:w-[150px] lg:w-[180px] h-auto select-none shrink-0"
        draggable={false}
        data-testid="idea-logo"
      />
      <span
        aria-hidden="true"
        className="h-8 md:h-10 w-px shrink-0"
        style={{ background: "rgba(255,255,255,0.14)" }}
        data-testid="brand-divider"
      />
      <div className="flex flex-col leading-tight min-w-0">
        <span
          className="text-sm md:text-lg tracking-[0.28em] font-semibold whitespace-nowrap"
          style={{ color: "var(--accent)" }}
        >
          LIVE&nbsp;BUILD
        </span>
        <span
          className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] md:text-[11px] tracking-[0.24em] text-white/45 uppercase font-normal whitespace-nowrap"
          data-testid="private-session-label"
        >
          <ShieldCheck size={11} className="opacity-70 shrink-0" />
          {useLang().t("private_session")}
        </span>
      </div>
    </div>
  );
}

function LangToggle({ lang, setLang }) {
  const btn = (code) => (
    <button
      key={code}
      type="button"
      onClick={() => setLang(code)}
      className="px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-colors"
      style={{
        color: lang === code ? "#000" : "rgba(255,255,255,0.75)",
        background: lang === code ? "var(--accent)" : "transparent",
        borderRadius: 4,
      }}
      data-testid={`lang-${code.toLowerCase()}`}
    >
      {code}
    </button>
  );
  return (
    <div
      className="inline-flex items-center gap-0.5 p-0.5 rounded-md border"
      style={{
        borderColor: "rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.02)",
      }}
      data-testid="lang-toggle"
    >
      {btn("AR")}
      {btn("EN")}
    </div>
  );
}

function SecureConnectionBadge({ status, signed }) {
  const { t } = useLang();
  const active = status !== "COMPLETED";
  const label =
    status === "LIVE" ? t("status_live") : status === "COMPLETED" ? t("status_completed") : status === "PAUSED" ? t("status_paused") : t("status_waiting");
  const color =
    status === "LIVE"
      ? "#ff3333"
      : status === "COMPLETED"
      ? "rgba(255,255,255,0.55)"
      : "var(--accent)";
  return (
    <div
      className="inline-flex items-center gap-3 pl-3 pr-3 py-1.5 rounded-full border"
      style={{
        borderColor: active ? "rgba(0,229,255,0.35)" : "rgba(255,255,255,0.12)",
        background: active ? "rgba(0,229,255,0.05)" : "rgba(255,255,255,0.02)",
      }}
      data-testid="secure-connection-badge"
    >
      <span className="inline-flex items-center gap-2">
        {status === "LIVE" ? (
          <span className="live-dot" />
        ) : status === "COMPLETED" ? (
          <CheckCircle2 size={12} style={{ color: "var(--success)" }} />
        ) : (
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: "var(--accent)" }}
          />
        )}
        <span
          className="text-[11px] font-semibold tracking-[0.24em]"
          style={{ color: "#fff" }}
          data-testid="secure-status-label"
        >
          {label}
        </span>
      </span>
      <span
        className="hidden md:inline text-[10px] font-medium tracking-[0.24em] pl-3 border-l"
        style={{
          color: "rgba(255,255,255,0.55)",
          borderColor: "rgba(255,255,255,0.1)",
        }}
      >
        {signed ? t("secure_connection") : t("secure_session")}
      </span>
      <ShieldCheck
        size={13}
        className="hidden md:inline"
        style={{ color: active ? "var(--accent)" : "rgba(255,255,255,0.4)" }}
      />
    </div>
  );
}

// ---------- Info tile / bar ----------
function initialsOf(name) {
  if (!name) return "•";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "•";
}

/** Small circular avatar shown next to the technician name. Falls back to
 *  initials on missing/empty URL AND on <img> load error. Pure visual add. */
function TechAvatar({ name, photoUrl, size = 36 }) {
  const [failed, setFailed] = React.useState(false);
  const show = !!photoUrl && !failed;
  return (
    <span
      className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
      style={{
        width: size, height: size,
        border: "1px solid rgba(0,229,255,0.35)",
        background: "rgba(0,229,255,0.06)",
        boxShadow: "0 0 8px rgba(0,229,255,0.15)",
      }}
      data-testid="technician-avatar"
    >
      {show ? (
        <img
          src={photoUrl}
          alt={name || "Technician"}
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
          data-testid="technician-avatar-img"
        />
      ) : (
        <span
          className="text-white text-[12px] font-semibold tracking-wide"
          data-testid="technician-avatar-initials"
        >
          {initialsOf(name)}
        </span>
      )}
    </span>
  );
}

function InfoTile({ icon: Icon, label, value, testid, leading }) {
  return (
    <div className="flex items-center gap-3 flex-1 min-w-0" data-testid={testid}>
      {leading ? (
        leading
      ) : (
        <span
          className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0"
          style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.02)", color: "var(--accent)" }}
        >
          <Icon size={16} />
        </span>
      )}
      <div className="min-w-0">
        <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 font-medium">
          {label}
        </div>
        <div className="mt-0.5 text-white text-[15px] font-medium truncate">
          {value || <span className="text-white/35">—</span>}
        </div>
      </div>
    </div>
  );
}

// ---------- Card ----------
function Card({ children, className = "", ...rest }) {
  return (
    <div
      className={`rounded-xl border p-5 md:p-6 ${className}`}
      style={{
        borderColor: "rgba(255,255,255,0.08)",
        background: "#0d0d10",
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ---------- Horizontal 4-step progress ----------
function HorizontalProgress({ status }) {
  const { t } = useLang();
  const activeIdx = status === "LIVE" || status === "PAUSED" ? 1
                  : status === "TESTING" ? 3
                  : status === "COMPLETED" ? 4 : 0;
  const steps = [
    t("step_waiting"),
    t("step_live"),
    t("step_completed"),
    t("step_testing"),
  ];
  return (
    <div className="flex items-start justify-between gap-1" data-testid="progress-timeline">
      {steps.map((label, i) => {
        const done = i < activeIdx;
        const active = i === activeIdx;
        const nextDone = i + 1 <= activeIdx;
        const stepKey = ["waiting", "live", "completed", "testing"][i];
        return (
          <React.Fragment key={i}>
            <div
              className="flex flex-col items-center flex-shrink-0 w-16 md:w-20"
              data-testid={`step-${stepKey}`}
            >
              <div className="relative">
                <div
                  className="w-9 h-9 md:w-10 md:h-10 rounded-full border flex items-center justify-center text-sm relative font-semibold"
                  style={{
                    borderColor: active
                      ? "var(--accent)"
                      : done
                      ? "rgba(0,229,255,0.45)"
                      : "rgba(255,255,255,0.18)",
                    background: active
                      ? "rgba(0,229,255,0.1)"
                      : done
                      ? "rgba(0,229,255,0.04)"
                      : "transparent",
                    color: active
                      ? "var(--accent)"
                      : done
                      ? "rgba(0,229,255,0.9)"
                      : "rgba(255,255,255,0.4)",
                    boxShadow: active ? "0 0 0 4px rgba(0,229,255,0.08)" : "none",
                  }}
                >
                  {done ? <CheckCircle2 size={16} /> : i + 1}
                  {active && (
                    <span
                      className="absolute inset-[-4px] rounded-full"
                      style={{
                        background: "var(--accent)",
                        opacity: 0.28,
                        animation: "progressPulse 1.6s ease-out infinite",
                      }}
                    />
                  )}
                </div>
              </div>
              <div
                className={`mt-3 text-[10px] md:text-xs text-center leading-tight whitespace-pre-line ${
                  active
                    ? "font-medium"
                    : done
                    ? "text-white/60"
                    : "text-white/40"
                }`}
                style={active ? { color: "var(--accent)" } : {}}
              >
                {label}
              </div>
            </div>
            {i < 3 && (
              <div
                className="flex-1 h-px mt-[18px] md:mt-[20px]"
                style={{
                  background: nextDone
                    ? "linear-gradient(90deg, rgba(0,229,255,0.5), rgba(0,229,255,0.15))"
                    : "rgba(255,255,255,0.1)",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
      <style>{`
        @keyframes progressPulse {
          0%   { transform: scale(0.7); opacity: 0.32; }
          70%  { transform: scale(1.75); opacity: 0; }
          100% { transform: scale(1.75); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ---------- YOUR BUILD (dynamic) ----------
const COMPONENT_META = [
  { key: "cpu", label: "CPU", icon: Cpu },
  { key: "gpu", label: "GPU", icon: MonitorSmartphone },
  { key: "ram", label: "RAM", icon: MemoryStick },
  { key: "storage", label: "STORAGE", icon: HardDrive },
  { key: "motherboard", label: "MOTHERBOARD", icon: CircuitBoard },
  { key: "case", label: "CASE", icon: Package },
  { key: "power_supply", label: "PSU", icon: Zap },
  { key: "cpu_cooler", label: "COOLER", icon: Fan },
];

function YourBuildCard({ components }) {
  const { t } = useLang();
  const rows = COMPONENT_META.filter(
    (m) => (components?.[m.key] || "").toString().trim() !== ""
  );
  if (rows.length === 0) return null;
  return (
    <Card data-testid="your-build-card">
      <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-medium mb-4">
        {t("your_build")}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {rows.map(({ key, label, icon: Icon }) => (
          <div
            key={key}
            className="flex items-center gap-3 py-1.5"
            data-testid={`build-part-${key}`}
          >
            <span
              className="w-8 h-8 rounded-md border flex items-center justify-center shrink-0"
              style={{
                borderColor: "rgba(0,229,255,0.25)",
                background: "rgba(0,229,255,0.04)",
                color: "var(--accent)",
              }}
            >
              <Icon size={14} />
            </span>
            <span className="text-[10px] tracking-[0.24em] uppercase text-white/45 font-medium w-24 md:w-28 shrink-0">
              {label}
            </span>
            <span className="text-white text-sm md:text-[15px] font-medium truncate">
              {components[key]}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Chat (visual — same behavior as before) ----------
function ChatBlock() {
  const [msg, setMsg] = useState("");
  const send = (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    toast.message("Chat coming soon", {
      description: "Real-time technician chat will activate shortly.",
    });
    setMsg("");
  };
  return (
    <Card data-testid="chat-section">
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex items-start gap-3 md:min-w-[280px]">
          <span
            className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0"
            style={{
              borderColor: "rgba(0,229,255,0.35)",
              background: "rgba(0,229,255,0.05)",
              color: "var(--accent)",
            }}
          >
            <MessageSquare size={16} />
          </span>
          <div>
            <div className="text-sm md:text-base font-semibold tracking-tight text-white">
              TALK TO YOUR TECHNICIAN
            </div>
            <div className="mt-1 text-[12px] md:text-[13px] text-white/50 leading-snug">
              Have a note or question about your build?
              <br className="hidden md:block" />
              Send it directly to your technician.
            </div>
          </div>
        </div>
        <form onSubmit={send} className="flex-1 flex items-center gap-3">
          <div
            className="relative flex-1 flex items-center rounded-lg border h-11 pr-3"
            style={{
              borderColor: "rgba(255,255,255,0.1)",
              background: "#050507",
            }}
          >
            <input
              type="text"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Type your message…"
              className="flex-1 bg-transparent px-4 h-full text-sm text-white placeholder-white/30 focus:outline-none"
              data-testid="chat-input"
            />
            <button
              type="button"
              className="text-white/40 hover:text-white/70"
              tabIndex={-1}
              data-testid="chat-attach"
            >
              <Paperclip size={16} />
            </button>
          </div>
          <button
            type="submit"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-lg font-semibold text-[13px] transition-transform hover:-translate-y-[1px]"
            style={{ background: "var(--accent)", color: "#000" }}
            data-testid="chat-send"
          >
            <Send size={14} /> SEND
          </button>
        </form>
      </div>
    </Card>
  );
}

// ---------- Footer ----------
function TrustFooter() {
  const { t } = useLang();
  const item = (Icon, title, desc, testid) => (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span
        className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0"
        style={{
          borderColor: "rgba(255,255,255,0.1)",
          background: "rgba(255,255,255,0.02)",
          color: "var(--accent)",
        }}
      >
        <Icon size={15} />
      </span>
      <div>
        <div className="text-white text-[13px] font-semibold tracking-tight">
          {title}
        </div>
        <div className="text-white/45 text-[11px] mt-0.5">{desc}</div>
      </div>
    </div>
  );
  return (
    <div
      className="border-t"
      style={{ borderColor: "rgba(255,255,255,0.06)" }}
      data-testid="trust-footer"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 md:py-8 grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
        {item(ShieldAlert, t("footer_secure_title"), t("footer_secure_desc"), "footer-secure")}
        {item(Lock, t("footer_private_title"), t("footer_private_desc"), "footer-private")}
        {item(HelpCircle, t("footer_help_title"), t("footer_help_desc"), "footer-help")}
        <div className="hidden md:flex items-center justify-end gap-3">
          <img src="/idea-logo.png" alt="IDEA" className="h-8 w-auto opacity-90" draggable={false} />
          <span className="text-[10px] tracking-[0.28em] text-white/45 font-semibold">
            {t("footer_tagline")}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------- Completed hero ----------
function CompletedHero({ reportUrl }) {
  const { t } = useLang();
  return (
    <div
      className="w-full overflow-hidden rounded-2xl border relative"
      style={{
        aspectRatio: "16 / 9",
        borderColor: "rgba(0,255,102,0.2)",
        background:
          "radial-gradient(circle at 50% 45%, rgba(0,255,102,0.06), transparent 55%), #0a0a0d",
        boxShadow:
          "0 0 0 1px rgba(0,255,102,0.1), 0 30px 60px -30px rgba(0,255,102,0.15)",
      }}
      data-testid="assembly-completed-panel"
    >
      {/* Duplicate in-player COMPLETED badge removed — header shows the single status badge. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <div
          className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center border"
          style={{
            borderColor: "rgba(0,255,102,0.4)",
            background: "rgba(0,255,102,0.06)",
          }}
        >
          <CheckCircle2 size={32} style={{ color: "var(--success)" }} />
        </div>
        <div
          className="completed-headline mt-6 text-white text-[16px] leading-tight md:text-3xl font-semibold tracking-tight"
          data-testid="completed-headline"
        >
          {t("build_completed")}
        </div>
        {reportUrl && (
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-lg font-semibold text-[13px] tracking-[0.16em] transition-transform hover:-translate-y-[1px]"
            style={{ background: "var(--accent)", color: "#000" }}
            data-testid="view-final-build-report-button"
          >
            <FileText size={16} /> VIEW FINAL BUILD REPORT
          </a>
        )}
      </div>
    </div>
  );
}

// ---------- Page ----------
export default function CustomerLive() {
  const [lang, setLang] = useState(() => readStoredLang());
  const setLangPersist = (v) => { writeStoredLang(v); setLang(v); };
  return (
    <LangProvider lang={lang}>
      <CustomerLiveInner lang={lang} setLang={setLangPersist} />
    </LangProvider>
  );
}

function CustomerLiveInner({ lang, setLang }) {
  const { t } = useLang();
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [testing, setTesting] = useState(null);

  const fetchState = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/live/${token}`);
      setState({ loading: false, data, error: null });
      return data;
    } catch (e) {
      setState({
        loading: false,
        data: null,
        error:
          e.response?.status === 404
            ? t("link_invalid")
            : t("link_load_error"),
      });
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Optional URL override for the reconnect interval so QA can verify the
  // full cycle in seconds instead of minutes. Production default is 60 000 ms.
  // Bounds: minimum 3 000 ms, maximum 10 minutes.
  const playerReconnectMs = React.useMemo(() => {
    if (typeof window === "undefined") return 60000;
    const q = new URLSearchParams(window.location.search).get("playerReconnectMs");
    const n = q ? parseInt(q, 10) : NaN;
    if (!Number.isFinite(n)) return 60000;
    return Math.min(600000, Math.max(3000, n));
  }, []);

  // Fire-and-forget beacon on every 60 s auto-reconnect. Server logs
  // PLAYER_AUTO_RECONNECT with order_id + timestamp only — never the token.
  // Declared at the top-level of this component (NEVER inside a conditional
  // JSX branch) so hooks are always called in the same order.
  const onPlayerAutoReconnect = React.useCallback(() => {
    axios
      .post(`${API}/live/${token}/player-reconnect`, {}, { timeout: 4000 })
      .catch(() => {});
  }, [token]);

  const fetchTesting = async () => {
    try {
      const { data } = await axios.get(`${API}/live/${token}/testing`);
      setTesting(data || null);
    } catch { /* not started */ }
  };

  useEffect(() => {
    fetchState();
    fetchTesting();
    const id = setInterval(fetchState, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, lang]);

  // Refetch the testing snapshot whenever the live status flips into TESTING
  // (initial value may have been null before assembly finished).
  useEffect(() => {
    if (state.data?.status === "TESTING" || state.data?.status === "COMPLETED") {
      fetchTesting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data?.status]);

  const onSystemMessage = React.useCallback((p) => {
    if (p.type !== "testing_state" && p.type !== "testing_temp") return;
    // Authoritative refetch — the WS payload's `testing` field is a hint that
    // something changed; always pull the latest server state to eliminate any
    // stale-closure or partial-patching class of bugs.
    fetchTesting();
  }, []);

  // Fire "customer opened live view" beacon once per browser session.
  // Failures MUST NOT interrupt the video / UI in any way.
  useEffect(() => {
    if (!token) return;
    const key = `idea_live_opened_${token}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* private mode: ignore */ }
    axios.post(`${API}/live/${token}/opened`, {}, { timeout: 4000 }).catch(() => {});
  }, [token]);

  const dir = lang === "AR" ? "rtl" : "ltr";

  if (state.loading) {
    return (
      <div dir={dir} className="customer-live min-h-screen flex items-center justify-center text-white/40 text-sm">
        {t("loading")}
      </div>
    );
  }

  if (state.error) {
    return (
      <div dir={dir} className="customer-live min-h-screen flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <BrandRow />
          <div className="mt-10 text-2xl text-white font-semibold">{state.error}</div>
          <p className="mt-3 text-white/50 text-sm">
            {t("link_help")}
          </p>
        </div>
      </div>
    );
  }

  const d = state.data;
  // `isPaused` / `isTesting` reflect the raw backend status and drive the
  // TestingPanel visibility (see below). The header and headline both derive
  // from the SINGLE authoritative `displayStatus` — never from raw status —
  // so `revoked` and `COMPLETED` can never produce a stale TESTING message.
  const isPaused = d.status === "PAUSED";
  const isTesting = d.status === "TESTING";
  const completedOrRevoked = d.status === "COMPLETED" || d.revoked;
  const displayStatus = completedOrRevoked ? "COMPLETED" : (isPaused ? "PAUSED" : isTesting ? "TESTING" : d.status);
  const isLive = displayStatus === "LIVE";
  const isWaiting = displayStatus === "WAITING";
  const isCompleted = displayStatus === "COMPLETED";
  const signed = !!d.playback?.signed;

  // Headline mapping derives from `displayStatus` exclusively so it can never
  // contradict the status pill / player panel. COMPLETED (or revoked) takes
  // precedence over any lingering TESTING copy.
  const headlineKey =
    displayStatus === "WAITING"  ? "headline_waiting"  :
    displayStatus === "PAUSED"   ? "headline_paused"   :
    displayStatus === "LIVE"     ? "headline_live"     :
    displayStatus === "TESTING"  ? "headline_testing"  :
                                   "headline_completed";
  const highlightColor = displayStatus === "PAUSED" ? "#ff9800" : "var(--accent)";
  const headline = (
    <>
      {t(`${headlineKey}_prefix`)}
      <span style={{ color: highlightColor }}>{t(`${headlineKey}_highlight`)}</span>
    </>
  );

  return (
    <div dir={dir} className="customer-live min-h-screen relative flex flex-col" style={{ background: "#050507" }}>
      <div
        className="absolute top-0 left-0 right-0 h-px pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(0,229,255,0.45), transparent)",
        }}
      />

      <div className="relative flex-1 max-w-6xl mx-auto w-full px-5 md:px-8 pt-6 md:pt-8 pb-10">
        {/* Header */}
        <div
          className="flex items-center justify-between gap-4 flex-wrap"
          data-testid="customer-header"
        >
          <BrandRow />
          <div className="flex items-center gap-2 md:gap-3">
            <LangToggle lang={lang} setLang={setLang} />
            <SecureConnectionBadge status={displayStatus} signed={signed} />
          </div>
        </div>

        {/* Player */}
        <div className="mt-6 md:mt-8">
          {isCompleted ? (
            <CompletedHero
              reportUrl={
                d.report_token
                  ? `${typeof window !== "undefined" ? window.location.origin : ""}/report/${d.report_token}`
                  : null
              }
            />
          ) : (
            <LivePlayer
              playback={d.playback}
              status={d.status}
              revoked={!!d.revoked}
              broadcastKey={`${d.order_number || ""}:${d.start_time || ""}`}
              refreshPlayback={fetchState}
              onAutoReconnect={onPlayerAutoReconnect}
              autoReconnectMs={playerReconnectMs}
              overlaySlot={d.status === "TESTING" ? <TestingVideoOverlay testing={testing} /> : null}
            />
          )}
        </div>

        {/* Headline */}
        <div className="mt-8 md:mt-10">
          <h1
            className="text-white text-3xl md:text-5xl font-semibold leading-[1.05] tracking-tight"
            data-testid="customer-headline"
          >
            {headline}
          </h1>
        </div>

        {/* Info row: ORDER | STATION | TECHNICIAN */}
        <div
          className="mt-6 md:mt-7 rounded-xl border flex flex-col md:flex-row md:items-center divide-y md:divide-y-0 md:divide-x divide-white/[0.08] md:divide-white/[0.08]"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }}
        >
          <div className="p-5 md:p-6 md:flex-1">
            <InfoTile
              icon={ClipboardList}
              label={t("label_order")}
              value={`#${d.order_number}`}
              testid="info-order"
            />
          </div>
          <div className="p-5 md:p-6 md:flex-1">
            <InfoTile
              icon={MonitorSmartphone}
              label={t("label_station")}
              value={d.station?.id || "ST01"}
              testid="info-station"
            />
          </div>
          <div className="p-5 md:p-6 md:flex-1">
            <InfoTile
              label={t("label_technician")}
              value={d.technician?.name || d.station?.technician_name || ""}
              testid="info-technician"
              leading={<TechAvatar name={d.technician?.name || d.station?.technician_name} photoUrl={resolveApiUrl(d.technician?.profile_photo_url || d.station?.profile_photo_url)} />}
            />
          </div>
        </div>

        {/* Build Progress + Build Time */}
        <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-5 gap-5 md:gap-6">
          <Card className="lg:col-span-3" data-testid="progress-card">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-medium mb-5">
              {t("label_build_progress")}
            </div>
            <HorizontalProgress status={displayStatus} />
          </Card>

          <Card className="lg:col-span-2" data-testid="timer-card">
            <div className="flex items-center gap-2 mb-4">
              <Clock size={14} className="text-white/45" />
              <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 font-medium">
                {t("label_build_time")}
              </div>
            </div>
            <div dir="ltr" className="inline-block">
              <BuildTimer
                startTime={d.start_time}
                endTime={isLive ? null : d.finish_time}
                running={isLive}
                size="lg"
              />
            </div>
            <div className="mt-3 text-[11px] text-white/40 font-medium">
              {isWaiting
                ? t("timer_waiting")
                : isLive
                ? t("timer_live")
                : t("timer_completed")}
            </div>
          </Card>
        </div>

        {/* YOUR BUILD */}
        <div className="mt-6 md:mt-8">
          <YourBuildCard components={d.components} />
        </div>

        {/* TESTING & QUALITY CHECK — appears when technician has finished assembly */}
        {(isTesting || (isCompleted && testing)) && (
          <div className="mt-6 md:mt-8">
            <CustomerTestingPanel testing={testing} />
          </div>
        )}

        {/* Chat */}
        <div className="mt-6 md:mt-8">
          <ChatBlockCustomer token={token} status={displayStatus} revoked={d.revoked} onSystemMessage={onSystemMessage} />
        </div>

        {/* Technician rating — only after FINISH */}
        {isCompleted && (
          <div className="mt-6 md:mt-8">
            <RatingSection
              token={token}
              status={displayStatus}
              technician={{
                name: d.technician?.name || d.station?.technician_name || "",
                profile_photo_url: resolveApiUrl(d.technician?.profile_photo_url || d.station?.profile_photo_url),
              }}
            />
          </div>
        )}
      </div>

      <TrustFooter />
    </div>
  );
}
